<template>
  <div class="flex flex-col items-center justify-center w-full pb-20 bg-[#F2F6FA]">
    <section class="flex flex-col items-center w-full justify-center  px-4">
      <div class="flex items-center justify-start md:mt-5 mb-0 mt-2 w-full ">
        <div class="items-center gap-2 mt-5 -ml-2 d-flex">
          <img src="@/assets/img/content_an.svg" class="cursor-pointer w-6 md:w-[28px]" alt="recommendations" />
          <p class="text-base md:text-[22px] font-bold">Profile Update Recommendations</p>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="text-center py-8 w-full max-w-3xl">
        <div class="flex flex-col items-center justify-center py-8">
          <v-progress-circular indeterminate color="primary" class="mb-4"></v-progress-circular>
          <p class="text-gray-600">Loading Google Business Profiles...</p>
        </div>
      </div>
      
      <!-- Empty State when no business details are set up -->
      <div v-else-if="businessDetailsMissing" class="text-center py-8 w-full max-w-3xl">
        <div class="bg-white rounded-lg shadow-sm p-8 mt-4">
          <EmptyState
            title="Set Up Your Business Details"
            message="To generate personalized recommendations, Fly needs to know more about your brand. Please set up your business details first."
            actionText="Manage Brand Details"
            icon="mdi-domain"
            iconColor="blue darken-2"
            @action="goToBusinessDetails"
          />
        </div>
      </div>
      
      <!-- Empty State when no locations connected -->
      <div v-else-if="!loading && locations.length === 0 && recommendations.length === 0" class="text-center py-8 w-full max-w-3xl">
        <div class="bg-white rounded-lg shadow-sm p-8 mt-4">
          <NoLocationsConnected
            title="No Google Business Profiles connected yet"
            message="Connect your Google Business Profiles to receive AI-powered optimization recommendations."
            actionText="Connect Google Locations"
            icon="mdi-google-my-business"
            iconColor="amber darken-2"
            @click.native="handleConnectGMB"
          />
        </div>
      </div>

      <!-- Main Content -->
      <div v-else class="w-full">
        <!-- Recent Recommendations Card -->
        <div class="bg-white rounded-lg shadow-sm p-6 my-6">
          <div class="flex justify-between items-center mb-4">
            <!-- <h2 class="text-lg font-semibold">Recent Recommendations</h2> -->
            <div class="flex space-x-2">
              <div class="flex space-x-2 status-filters">
                <button
                  v-for="option in [null, 'high', 'medium', 'low', 'inactive']"
                  :key="option || 'all'"
                  @click="filterByPriority(option)"
                  class="status-filter-btn"
                  :class="[
                    {
                      'bg-blue-100 text-blue-800 border-blue-200': option === null,
                      'bg-red-100 text-red-700 border-red-200': option === 'high',
                      'bg-orange-100 text-orange-700 border-orange-200': option === 'medium',
                      'bg-green-100 text-green-700 border-green-200': option === 'low',
                    },
                    { active: priorityFilter === option }
                  ]"
                >
                  {{ option === null ? 'All (Active)' : option === 'inactive' ? 'Inactive' : option.charAt(0).toUpperCase() + option.slice(1) }} 
                  <span class="ml-1">({{ getPriorityCount(option) }})</span>
                </button>
              </div>
            </div>
          </div>

          <div v-if="loading" class="flex justify-center py-6">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="filteredRecommendations.length === 0 && priorityFilter !== 'inactive'" class="text-center py-8">
            <EmptyState
              title="No Recommendations Yet"
              message="We're working on analyzing your profiles. Check back soon for personalized recommendations to improve your Google Business Profiles."
              icon="mdi-clipboard-check-outline"
              iconColor="blue lighten-1"
            ></EmptyState>
          </div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Recommendation Card -->
            <div v-for="recommendation in filteredRecommendations" :key="recommendation.id"
              class="bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              :class="{ 'opacity-60': recommendation.status !== 'active' }"
              @click="openRecommendationDetails(recommendation)">
              <div class="p-4">
                <div class="flex justify-between items-start mb-2">
                  <span
                    class="text-xs font-medium rounded-full px-2 py-1"
                    :class="{
                      'bg-red-100 text-red-700': recommendation.status === 'active' && recommendation.priority === 'high',
                      'bg-orange-100 text-orange-700': recommendation.status === 'active' && (recommendation.priority === 'medium' || !recommendation.priority),
                      'bg-green-100 text-green-700': recommendation.status === 'active' && recommendation.priority === 'low',
                      'bg-gray-200 text-gray-600': recommendation.status !== 'active'
                    }"
                  >
                  {{ recommendation.status === 'active' ? 
                    (recommendation.priority ? (recommendation.priority.charAt(0).toUpperCase() + recommendation.priority.slice(1)) : 'Medium') + ' Priority' : 
                    'Inactive' }}
                  </span>
                  <span v-if="recommendation.connection === 'shallow'" 
                        class="text-xs font-medium rounded-full px-2 py-1 bg-amber-100 text-amber-700">
                    Partial Access
                  </span>
                  <span v-else-if="recommendation.actionTaken" 
                        class="text-xs font-medium rounded-full px-2 py-1 bg-green-100 text-green-700">
                    Applied
                  </span>
                </div>

                <h3 class="font-semibold mb-1 text-lg text-[#333]">
                  {{ getRecommendationTitle(recommendation) }}
                </h3>

                <div v-if="isMultiLocation(recommendation)" class="text-sm text-gray-600 mb-2">
                  {{ getLocationCount(recommendation) }} locations affected
                </div>
                <div v-else class="text-sm text-gray-600 mb-2">
                  Location: {{ getLocationName(recommendation).name }}
                </div>

                <div class="mt-2 space-y-2 line-clamp-3">
                  <div v-if="recommendation.location && recommendation.location.current_value !== undefined" class="text-sm">
                    <span class="text-gray-500">Current:</span>
                    <span class="font-medium">
                      {{ recommendation.field=='primary_category'?getCategoryNames(recommendation.location.current_value): recommendation.location.current_value || 'Not set' }}
                    </span>
                  </div>

                  <div v-if="recommendation.location && recommendation.location.recommended_value" class="text-sm">
                    <span class="text-gray-500">Recommended:</span>
                    <span class="font-medium text-green-700">
                      {{ recommendation.field=='primary_category'?getCategoryNames(recommendation.location.recommended_value): recommendation.location.recommended_value }}
                    </span>
                  </div>

                  <div v-if="recommendation.consistency_analysis && recommendation.consistency_analysis.reason" class="text-sm text-gray-600">
                    {{ recommendation.consistency_analysis.reason }}
                  </div>
                </div>

                <div class="flex justify-between items-center mt-4 gap-2 w-full">
                  <div v-if="recommendation.date" class="text-xs text-gray-500">
                    {{ new Date(recommendation.date.seconds * 1000).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) }}
                  </div>
                  <div class="d-flex space-x-2">
                    <v-btn 
                      small 
                      outlined 
                      color="primary" 
                      @click.stop="openRecommendationDetails(recommendation)">
                      View Details
                    </v-btn>
                    <v-btn 
                      v-if="!recommendation.actionTaken && recommendation.status === 'active' && recommendation.connection !== 'shallow' && !isPhoneWithoutRecommendation(recommendation)"
                      small 
                      color="primary" 
                      :loading="applyingRecommendation === recommendation.id" 
                      :disabled="applyingRecommendation !== null"
                      @click.stop="applyRecommendation(recommendation)">
                      Apply
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="filteredRecommendations.length > 0 && !allLoaded" class="flex justify-center mt-6">
            <v-btn text color="primary" @click="loadMoreRecommendations">
              <v-icon left>mdi-chevron-down</v-icon>
              Load More
            </v-btn>
          </div>
        </div>

        <!-- SEO Dashboard -->
        <SEORecommendationsDashboard :locations="locations" class="mb-6" />
      </div>
    </section>

    <!-- Recommendation Detail Dialog -->
    <v-dialog v-model="showDialog" max-width="700px" scrollable>
      <v-card v-if="currentRecommendation">
        <v-card-title class="d-flex justify-space-between align-center">
          <div class="flex items-center gap-3">
            <span>{{ getRecommendationTitle(currentRecommendation) }}</span>
            <span class="text-xs px-2 py-1 rounded-full"
              :class="currentRecommendation.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'">
              {{ currentRecommendation.status === 'active' ? 'Active' : 'Inactive' }}
            </span>
            <span v-if="currentRecommendation.connection === 'shallow'"
              style="background-color: #e6f7ff; color: #0072b1;" class="text-xs px-2 py-1 rounded-full">
              Shallow Connected
            </span>
          </div>
          <v-btn icon @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Recommendation Details based on type -->
          <div class="border rounded-lg p-4 bg-gray-50">
            <!-- Original attributes recommendations -->
            <div v-if="currentRecommendation.field_name === 'attributes'">
              <h4 class="font-medium text-gray-700 mb-2">Current Attributes</h4>
              <div class="mb-4 ml-4">
                <div class="attribute-list text-gray-600">
                  {{ currentRecommendation.location.attributes.join(', ') || 'None' }}
                </div>
              </div>

              <h4 class="font-medium text-gray-700 mb-2">Recommended Attributes</h4>
              <div class="ml-4 flex flex-wrap gap-2">
                <span v-for="attr in currentRecommendation.context.recommended_attributes" :key="attr"
                  class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  {{ attr }}
                </span>
              </div>
            </div>

            <!-- Original hours recommendations -->
            <div v-else-if="currentRecommendation.field_name === 'hours'">
              <h4 class="font-medium text-gray-700 mb-2">Current Hours</h4>
              <div class="mb-4 ml-4">
                <ul class="space-y-1 text-gray-600">
                  <li v-for="(hours, day) in currentRecommendation.location.hours" :key="day">
                    {{ capitalize(day) }}: {{ hours.open }} - {{ hours.close }}
                  </li>
                </ul>
              </div>

              <h4 class="font-medium text-gray-700 mb-2">Recommended Hours</h4>
              <div class="ml-4 text-gray-800">
                <p class="mb-1"><span class="font-medium">Weekdays:</span>
                  {{ currentRecommendation.context.recommended_hours.weekday.open }} -
                  {{ currentRecommendation.context.recommended_hours.weekday.close }}
                </p>
                <p><span class="font-medium">Weekends:</span>
                  {{ currentRecommendation.context.recommended_hours.weekend.open }} -
                  {{ currentRecommendation.context.recommended_hours.weekend.close }}
                </p>
              </div>
            </div>

            <!-- Field value recommendations -->
            <div v-else-if="currentRecommendation.field">
              <!-- Move Priority to top -->
              <div v-if="currentRecommendation.priority" class="mb-4">
                <h4 class="font-medium text-gray-700 mb-2">Priority</h4>
                <p class="ml-4" :class="getPriorityClass(currentRecommendation.priority)">
                  {{ capitalize(currentRecommendation.priority) }}
                </p>
              </div>

              <!-- Move Overall Analysis to top -->
              <div v-if="currentRecommendation.consistency_analysis" class="mb-4">
                <h4 class="font-medium text-gray-700 mb-2">Overall Analysis</h4>
                <p class="ml-4 text-gray-700 mb-4">
                  {{ currentRecommendation.consistency_analysis.reason }}
                </p>

                <div v-if="currentRecommendation.consistency_analysis.consistency_score !== undefined"
                  class="ml-4 flex items-center mb-4">
                  <span class="text-gray-600 mr-2">Consistency Score:</span>
                  <div class="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div class="bg-blue-600 h-2 rounded-full"
                      :style="`width: ${currentRecommendation.consistency_analysis.consistency_score * 100}%`"></div>
                  </div>
                  <span>{{ (currentRecommendation.consistency_analysis.consistency_score * 100).toFixed(0) }}%</span>
                </div>
              </div>
              <hr class="my-4 border-gray-300" />
              <!-- Overall reason for the recommendation moved below priority and analysis -->

              <!-- Group locations with similar recommendations -->
              <div v-for="(group, groupIndex) in getGroupedLocations(currentRecommendation)" :key="groupIndex"
                class="mb-6 p-4 bg-gray-50 rounded-lg">

                <!-- Use vertical layout for descriptions or long text -->
                <template v-if="isLongText(group.current_value) || isLongText(group.recommended_value) ||
                  currentRecommendation.field === 'description'">
                  <div class="mb-4">
                    <h6 class="text-gray-600 font-medium mb-2">Current:</h6>
                    <div class="bg-gray-100 p-3 rounded">
                      {{ currentRecommendation.field=='primary_category'?getCategoryNames(group.current_value):
                      group.current_value || 'Not set' }}
                    </div>
                  </div>

                  <div class="mb-4">
                    <h6 class="text-gray-600 font-medium mb-2">Recommended:</h6>
                    <div class="bg-green-100 text-green-800 p-3 rounded">
                      {{ currentRecommendation.field=='primary_category'?getCategoryNames(group.recommended_value):
                      (group.recommended_value || (group.recommended_value === null ?
                        getAddFieldText(currentRecommendation.field) : 'Not available')) }}
                    </div>
                  </div>
                </template>

                <!-- Use horizontal layout for short text -->
                <template v-else>
                  <div class="flex items-center mb-2">
                    <span class="text-gray-600 w-32">Current:</span>
                    <span class="bg-gray-100 px-2 py-1 rounded">
                      {{ currentRecommendation.field=='primary_category'?getCategoryNames(group.current_value):
                      group.current_value || 'Not set' }}
                    </span>
                  </div>
                  <div class="flex items-center mb-4">
                    <span class="text-gray-600 w-32">Recommended:</span>
                    <span class="bg-green-100 text-green-800 px-2 py-1 rounded">
                      {{ currentRecommendation.field=='primary_category'?getCategoryNames(group.recommended_value):
                      (group.recommended_value || (group.recommended_value === null ?
                        getAddFieldText(currentRecommendation.field) : 'Not available')) }}
                    </span>
                  </div>
                </template>

                <h6 class="text-sm font-medium text-gray-700 mb-2">Affected Locations ({{ group.locations.length }}):
                </h6>
                <div class="ml-4 flex flex-col gap-1">
                  <div v-for="(location, locIndex) in group.locations" :key="locIndex"
                    class="text-sm text-gray-700 mb-2">
                    • <span class="font-medium">{{ getLocationName(location).name }}</span>
                    <v-tooltip bottom max-width="800">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-if="getLocationName(location).address"
                          class="ml-3 text-gray-500 text-xs mt-1 truncate max-w-[500px] cursor-pointer" v-bind="attrs"
                          v-on="on">
                          {{ getLocationName(location).address }}
                        </div>
                      </template>
                      <span>{{ getLocationName(location).address }}</span>
                    </v-tooltip>
                  </div>
                </div>
                <hr class="my-4 border-gray-300" />
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showDialog = false">Cancel</v-btn>
          <v-btn
            v-if="!currentRecommendation.actionTaken && currentRecommendation.status === 'active' && currentRecommendation.connection !== 'shallow' && !isPhoneWithoutRecommendation(currentRecommendation)"
            :color="applyingRecommendation !== null ? 'grey' : 'primary'"
            :loading="applyingRecommendation === currentRecommendation.id" :disabled="applyingRecommendation !== null"
            @click="applyRecommendation(currentRecommendation)">
            {{ applyingRecommendation === currentRecommendation.id ? 'Applying...' : 'Apply Recommendation' }}
          </v-btn>
          <v-btn v-else-if="isPhoneWithoutRecommendation(currentRecommendation)" color="gray" text disabled>
            Not applicable
          </v-btn>
          <v-btn v-else-if="currentRecommendation.actionTaken" color="success"
            @click="showUpdateResultsDialog(currentRecommendation)">
            <v-icon small left>mdi-check-circle</v-icon>
            View Results
          </v-btn>
          <v-btn v-else color="gray" text disabled>
            <v-icon small left>mdi-close-circle</v-icon>
            Recommendation Inactive
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showUpdateResults" max-width="600px" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Update Results</span>
          <v-btn icon @click="showUpdateResults = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-4 flex gap-4">
            <v-chip outlined color="success">
              Success: {{ successCount }}
            </v-chip>
            <v-chip outlined color="error">
              Failed: {{ failedCount }}
            </v-chip>
          </div>
          <v-data-table :headers="[
            { text: 'Location', value: 'title' },
            { text: 'Status', value: 'status' }
          ]" :items="updateResults" :items-per-page="10" :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
          }" dense>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="item.status === 'success' ? 'success' : 'error'" text-color="white">
                {{ item.status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { auth, db, functions } from '@/utils/firebase.utils';
import { collection, getDocs, query, orderBy, limit, startAfter, where, onSnapshot, addDoc, updateDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import InfiniteLoading from 'vue-infinite-loading';
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import NoLocationsConnected from "@/components/ui/common/NoLocationsConnected.vue";
import EmptyState from "@/components/ui/common/EmptyState.vue";
import SEORecommendationsDashboard from "@/components/dashboards/SEORecommendationsDashboard.vue";

export default {
  name: 'RecommendationPage',
  mixins: [connectedAppsMixins],
  components: {
    InfiniteLoading,
    NoLocationsConnected,
    EmptyState,
    SEORecommendationsDashboard
  },
  data() {
    return {
      locations: [],
      recommendations: [],
      categories:[],
      loading: true,
      applyingRecommendation: null,
      allLoaded: false,
      lastDoc: null,
      batchSize: 10,
      unsubscribe: null,
      filterOption: 'latest',
      filterOptions: [
        { text: 'Latest', value: 'latest' },
        { text: 'Oldest', value: 'oldest' },
      ],
      showDialog: false,
      showUpdateResults: false,
      currentRecommendation: null,
      priorityFilter: null,
      businessDetailsMissing: false,
    };
  },
  mounted() {
    this.fetchCategories()
    this.fetchWorkspacedata();
    this.checkBusinessDetails();
    this.setupRecommendationsListener();
  },
  beforeDestroy() {
    // Clean up the snapshot listener when component is destroyed
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  computed: {
    successCount() {
      if (!this.currentRecommendation?.updateResults) return 0;
      return this.currentRecommendation.updateResults.filter(result => result.status === 'success').length;
    },
    failedCount() {
      if (!this.currentRecommendation?.updateResults) return 0;
      return this.currentRecommendation.updateResults.filter(result => result.status === 'failed').length;
    },
    hasErrors() {
      return this.failedCount > 0;
    },
    updateResults() {
      return this.currentRecommendation?.updateResults || [];
    },
    filteredRecommendations() {
      if (!this.priorityFilter) {
        // Show only active recommendations in "All" tab
        return this.recommendations.filter(rec => rec.status === 'active');
      }

      if (this.priorityFilter === 'inactive') {
        return this.recommendations.filter(rec => rec.status !== 'active');
      }

      return this.recommendations.filter(
        rec => rec.status === 'active' && (rec.priority || 'medium') === this.priorityFilter
      );
    },
    highPriorityCount() {
      return this.recommendations.filter(rec => rec.status === 'active' && (rec.priority || 'medium') === 'high').length;
    },
    mediumPriorityCount() {
      return this.recommendations.filter(rec => rec.status === 'active' && (rec.priority || 'medium') === 'medium').length;
    },
    lowPriorityCount() {
      return this.recommendations.filter(rec => rec.status === 'active' && (rec.priority || 'medium') === 'low').length;
    }
  },
  methods: {
    getPriorityCount(priority) {
      if (priority === null) {
        return this.recommendations.filter(rec => rec.status === 'active').length;
      } else if (priority === 'high') {
        return this.highPriorityCount;
      } else if (priority === 'medium') {
        return this.mediumPriorityCount;
      } else if (priority === 'low') {
        return this.lowPriorityCount;
      } else if (priority === 'inactive') {
        return this.recommendations.filter(rec => rec.status !== 'active').length;
      }
      return 0;
    },
    filterByPriority(priority) {
      this.priorityFilter = priority === this.priorityFilter ? null : priority;
    },
    getCategoryNames(name){
      const cagtegory = this.categories.find(category=>category.name==name);
      return cagtegory?.displayName||name;
    },
    async fetchCategories() {
      try {
        const gmbCategories = await fetch("https://firebasestorage.googleapis.com/v0/b/around-dev-1.appspot.com/o/gmb-categories%2Fgmb-categories.json?alt=media&token=cccd1123-19ac-4d68-a46a-56da6a0e481d")
        this.categories = await gmbCategories.json();
      } catch (error) {
        console.error('Error fetching GMB data:', error);
        this.$alert.show('Error fetching GMB data');
      }
    },
    checkTest(rec) {
      console.log(rec, "this is rec");
    },
    getAddress(locationId) {
      const foundedLocation = this.locations.find(item => item.gmbProfile.name?.includes(locationId));
      return `${foundedLocation?.storefrontAddress?.addressLines?.[0]}, ${foundedLocation?.storefrontAddress?.locality},
       ${foundedLocation?.storefrontAddress?.administrativeArea}`;
    },
    async fetchWorkspacedata() {
      let collectionRef = query(
        collection(db, "users", this.currentUID, "fly_workspaces"),
        where("storeWorkspace", "==", true),
        where("admin", "==", this.WID)
      );

      let snapshot = await getDocs(collectionRef);

      // Fetch all members
      this.locations = await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          try {
            let location = docSnapshot.data();
            location.wid = docSnapshot.id;
            // Skip if gmbProfile is not present
            if (!location) {
              console.log(`No gmbProfile found for document ID ${docSnapshot?.id}, skipping.`);
              return null;
            }

            return location; // Return the member data
          } catch (error) {
            console.error(`Error processing document ${docSnapshot?.id}:`, error);
            return null; // Skip on error
          }
        })
      );

      console.log(this.locations, "these are locations");
      return;
    },
    setFilter(filter) {
      this.filterOption = filter;
      this.resetAndFetchRecommendations();
    },

    setupRecommendationsListener() {
      if (this.unsubscribe) {
        this.unsubscribe();
      }

      this.recommendations = [];
      this.lastDoc = null;
      this.loading = true;
      this.allLoaded = false;

      const collectionRef = collection(
        db,
        "users",
        this.currentUID,
        "fly_workspaces",
        this.WID,
        "gmb-recommendations"
      );

      // Create query based on filter option
      const queryConstraints = this.getQueryConstraints();

      // Remove status filter
      // queryConstraints.push(where("status", "==", "active"));

      const recommendationsQuery = query(collectionRef, ...queryConstraints);

      // Set up real-time listener
      this.unsubscribe = onSnapshot(recommendationsQuery, (snapshot) => {
        if (snapshot.empty) {
          this.recommendations = [];
          this.loading = false;
          this.allLoaded = true;
          return;
        }

        const docs = snapshot.docs;
        this.lastDoc = docs[docs.length - 1];

        // Process the recommendations
        const newRecommendations = docs
          .filter(doc => {
            // Filter out recommendations without location_recommendations
            const data = doc.data();
            return data?.location_recommendations && data.location_recommendations.length > 0;
          })
          .map((doc) => {
            const data = doc.data();
            return this.processRecommendation(doc.id, data);
            return data;
          });
        console.log(newRecommendations, "these are new recommendations");
        this.recommendations = newRecommendations;
        this.loading = false;
        this.allLoaded = docs.length < this.batchSize;
      }, (error) => {
        console.error("Error fetching recommendations:", error);
        this.loading = false;
      });
    },

    getQueryConstraints() {
      const constraints = [limit(this.batchSize)];

      switch (this.filterOption) {
        case 'latest':
          constraints.push(orderBy('date', 'desc'));
          break;
        case 'oldest':
          constraints.push(orderBy('date', 'asc'));
          break;
        case 'priority':
          // First find high priority recommendations
          constraints.push(where('location_recommendations.0.recommended_values.priority', '==', 'high'));
          constraints.push(orderBy('date', 'desc'));
          break;
      }

      return constraints;
    },

    processRecommendation(id, data) {
      // Standardize recommendation format based on type
      const recommendation = {
        ...data,
        id: id,
        field: data.field || '',
        field_name: data.field_name || '',
        updateResults: data.updateResults || []
      };

      // Store all location recommendations
      recommendation.location_recommendations = data.location_recommendations || [];

      // Set priority from consistency_analysis if available
      if (data.consistency_analysis?.standardization_recommendations?.length > 0) {
        recommendation.priority = data.consistency_analysis.standardization_recommendations[0].priority || 'medium';
      }

      // Set impact score if available
      recommendation.impact_score = data.consistency_analysis?.overall_impact_score || 0.5;

      // Extract field information
      if (!recommendation.field && !recommendation.field_name) {
        recommendation.field = this.determineRecommendationField(data);
      }

      // Get the first location recommendation to display in the list view
      if (data.location_recommendations && data.location_recommendations.length > 0) {
        const firstLoc = data.location_recommendations[0] || {};
        recommendation.location = {
          location_id: firstLoc.location_id,
          name: firstLoc.name || 'Unknown Location',
          id: firstLoc.location_id
        };

        // Add current and recommended values for display
        if (firstLoc.current_value) {
          recommendation.location.current_value = firstLoc.current_value === "Missing" ? "" : firstLoc.current_value;
        }
        if (firstLoc.recommended_values?.recommended_value) {
          recommendation.location.recommended_value = firstLoc.recommended_values.recommended_value;
        }
      } else {
        // Set default values when location_recommendations is not available
        recommendation.location = {
          location_id: null,
          name: 'Unknown Location'
        };
      }

      // Add special handling for different recommendation types
      if (data.type === 'category') {
        recommendation.category_analysis = data.category_analysis;
        recommendation.category_completion_score = data.category_completion_score;
      } else if (data.recommended_value) {
        recommendation.recommended_value = data.recommended_value;
      }

      // Include consistency analysis
      recommendation.consistency_analysis = data.consistency_analysis;

      return recommendation;
    },

    determineRecommendationField(data) {
      // Check if location_recommendations exists before trying to access it
      if (!data.location_recommendations || data.location_recommendations.length === 0) {
        return 'unknown';
      }

      // Look at first location recommendation to determine the field
      const firstRec = data.location_recommendations[0];

      // Try to identify the field based on the recommended value or pattern
      if (firstRec.recommended_values?.recommended_value?.includes('http')) {
        return 'website';
      } else if (firstRec.current_value?.includes('Restaurant') ||
        firstRec.current_value?.includes('Store') ||
        firstRec.recommended_values?.recommended_value?.includes('Store')) {
        return 'category';
      } else if (firstRec.current_value?.length > 100 ||
        (firstRec.recommended_values?.recommended_value &&
          firstRec.recommended_values.recommended_value.length > 100)) {
        return 'description';
      } else if (firstRec.current_value?.includes('-') ||
        (firstRec.recommended_values?.recommended_value &&
          firstRec.recommended_values.recommended_value.includes('-'))) {
        return 'name';
      } else if (firstRec.current_value?.includes(' ') &&
        firstRec.current_value?.length < 20) {
        return 'phone';
      }

      // Default if we can't determine
      return 'field';
    },

    resetAndFetchRecommendations() {
      this.recommendations = [];
      this.lastDoc = null;
      this.loading = true;
      this.allLoaded = false;
      this.setupRecommendationsListener();
    },

    async loadMoreRecommendations() {
      try {
        if (this.allLoaded || !this.lastDoc) return;
        
        this.loading = true;
        
        const collectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "gmb-recommendations"
        );
        
        // Create query with the lastDoc as the starting point
        const queryConstraints = this.getQueryConstraints();
        queryConstraints.push(startAfter(this.lastDoc));
        
        const recommendationsQuery = query(collectionRef, ...queryConstraints);
        const snapshot = await getDocs(recommendationsQuery);
        
        if (snapshot.empty) {
          this.allLoaded = true;
          this.loading = false;
          return;
        }
        
        const docs = snapshot.docs;
        this.lastDoc = docs[docs.length - 1];
        
        // Process and add the new recommendations
        const newRecommendations = docs
          .filter(doc => {
            const data = doc.data();
            return data?.location_recommendations && data.location_recommendations.length > 0;
          })
          .map((doc) => {
            return this.processRecommendation(doc.id, doc.data());
          });
          
        this.recommendations = [...this.recommendations, ...newRecommendations];
        this.allLoaded = docs.length < this.batchSize;
      } catch (error) {
        console.error("Error loading more recommendations:", error);
      } finally {
        this.loading = false;
      }
    },

    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    formatDate(date) {
      if (!date) return 'N/A';

      // Convert Firestore timestamp to JavaScript Date
      let jsDate;
      if (date && typeof date === 'object' && date.seconds) {
        // This is a Firestore timestamp object
        jsDate = new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
      } else if (date instanceof Date) {
        // Already a JavaScript Date object
        jsDate = date;
      } else {
        // Try to convert other formats
        jsDate = new Date(date);
        if (isNaN(jsDate.getTime())) {
          return 'Invalid date';
        }
      }

      const options = {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };

      return jsDate.toLocaleDateString(undefined, options);
    },

    openRecommendationDetails(recommendation) {
      this.currentRecommendation = recommendation;
      this.showDialog = true;
    },

    async applyRecommendation(recommendation) {
      try {
        console.log(recommendation, "this is the recommendation");
        // Set applying state
        this.applyingRecommendation = recommendation.id;

        // Create array to track updates
        const updates = [];

        // Determine which field we're updating
        const fieldToUpdate = recommendation.field || recommendation.field_name;
        if (!fieldToUpdate) {
          this.$alert.show('Unable to determine field to update');
          return;
        }
        const changes = [];
        // Iterate through each location recommendation
        recommendation.location_recommendations.forEach(locRec => {
          // Skip if no recommended value or if current value matches recommendation
          if (!locRec.recommended_values?.recommended_value ||
            (locRec.current_value === locRec.recommended_values.recommended_value &&
              locRec.current_value !== "Missing")) {
            return;
          }
          // Find the matching location
          const location = this.locations.find(loc =>
            loc?.gmbProfile.name?.includes(locRec.location_id)
          );
          if (!location) {
            console.warn(`Location not found for ID: ${locRec.location_id}`);
            return;
          }

          // Create update fields object based on the field type
          const updateFields = {};
          const recommendedValue = locRec.recommended_values.recommended_value;

          switch (fieldToUpdate) {
            case 'website':
              updateFields.websiteUri = recommendedValue;
              break;
            case 'phone':
              updateFields.phoneNumbers = { primaryPhone: recommendedValue };
              break;
            case 'description':
              updateFields.profile = { description: recommendedValue };
              break;
            case 'name':
            case 'title':
              updateFields.title = recommendedValue;
              break;
            case 'hours':
              // Handle hours format conversion
              if (recommendation.context?.recommended_hours) {
                const formattedHours = this.formatBusinessHours(recommendation.context.recommended_hours);
                updateFields.regularHours = formattedHours;
              }
              break;
            case 'category':
            case 'primary_category':
              if (!updateFields.categories) updateFields.categories = {};
              updateFields.categories.primaryCategory = { name: recommendedValue };
              // Preserve existing additional categories
              if (location.gmbProfile.categories?.additionalCategories?.length) {
                updateFields.categories.additionalCategories =
                  location.gmbProfile.categories.additionalCategories.map(cat => cat.name);
              }
              break;
            case 'attributes':
              // Handle attributes update
              if (recommendation.context?.recommended_attributes) {
                updateFields.attributes = recommendation.context.recommended_attributes;
              }
              break;
            default:
              // For any other fields, just set the value directly
              updateFields[fieldToUpdate] = recommendedValue;
          }

          // Add to updates if we have fields to update
          if (Object.keys(updateFields).length > 0) {
            updates.push({
              locationId: `locations/${locRec.location_id}`,
              updateFields,
              displayName: locRec.name || 'Unknown Location',
              wid: locRec.wid,
              accountId: location.accountId
            });
          }
        });

        // Check if we have updates to apply
        if (updates.length === 0) {
          this.$snackbar.show('No changes to update');
          return;
        }
        // Prepare payload for the cloud function
        const payload = {
          uid: this.currentUID,
          workspace_id: this.WID,
          updates
        };
        // Call the cloud function to update GMB profiles
        const updateGMBProfile = httpsCallable(functions, "updateGMBDataFunction");
        const res = await updateGMBProfile(payload);

        if (res?.data?.results) {
          // Create a map of location names
          const locationMap = {};
          this.locations.forEach(location => {
            locationMap[location.wid] = location.storeLocation || location.title;
          });

          // Format results and store in the recommendation
          recommendation.updateResults = res.data.results.map(item => ({
            ...item,
            title: locationMap[item.wid] || item.displayName
          }));

          // Count failed updates
          const failedCount = recommendation.updateResults.filter(result => result.status === "failed").length;
          const successCount = recommendation.updateResults.length - failedCount;

          // Store update results in a separate document - FIX: Use setDoc instead of addDoc with doc reference
          const updatesDocRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "gmb-recommendations-updates",
            recommendation.id
          );

          await setDoc(updatesDocRef, {
            updateResults: recommendation.updateResults,
            requested_at: new Date(),
            updatedBy: auth.currentUser.displayName || "unknown",
          });
          const profileDocRef = await addDoc(
            collection(db, "users", this.currentUID, "fly_workspaces", this.WID, "gmb-update-history"),
            {
              requested_by: auth.currentUser.displayName || "unknown",
              title: "Requested through recommendation",
              type: 'recommended',
              successCount,
              failedCount,
              recommendation_id: recommendation.id,
              address: '',
              requested_at: new Date(),
            }
          );
          // Update the original recommendation document
          const recDocRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "gmb-recommendations",
            recommendation.id
          );

          await updateDoc(recDocRef, {
            updateError: failedCount > 0,
            actionTaken: true,
            successCount,
            failedCount,
            lastUpdatedAt: new Date(),
          });

          // Mark recommendation as applied
          recommendation.actionTaken = true;
          recommendation.updateError = failedCount > 0;

          // Show success message
          this.$snackbar.show(`Successfully applied updates to ${updates.length - failedCount} locations`);
          this.showDialog = false
          // Show results if any failed
          if (failedCount > 0) {
            this.currentRecommendation = recommendation;
            this.showUpdateResults = true;
          }
        }
      } catch (error) {
        console.error('Error applying recommendation:', error);
        this.$alert.show('Error updating locations: ' + error.message);
      } finally {
        // Clear applying state
        this.applyingRecommendation = null;
      }
    },

    getRecommendationTitle(rec) {
      if (rec.field_name) {
        return this.$options.filters.formatFieldName(rec.field_name);
      } else if (rec.field) {
        // Handle specific field types with appropriate titles
        switch (rec.field) {
          case 'website':
            return 'Website URL';
          case 'phone':
            return 'Phone Number';
          case 'primary_category':
            return 'Primary Category';
          case 'description':
            return 'Business Description';
          case 'name':
            return 'Business Name';
          default:
            return this.capitalize(rec.field);
        }
      } else if (rec.type === 'category') {
        return 'Category Optimization';
      } else if (rec.type === 'cross_location') {
        return 'Cross-Location Consistency';
      }
      return 'Recommendation';
    },

    isMultiLocation(rec) {
      return (rec.location_recommendations && rec?.location_recommendations?.length);
    },

    getLocationCount(rec) {
      if (rec.location_recommendations) {
        return rec.location_recommendations.length;
      }
      return rec.location ? 1 : 0;
    },
    getLocationName(rec) {
      if (rec?.location_id) {
        const location = this.locations.find(loc =>
          loc?.gmbProfile.name?.includes(rec?.location_id)
        );
        if (!location) return { name: 'Unknown Location', address: '' };

        const name = location?.gmbProfile?.title || location?.title || 'Unnamed Location';

        // Check if address components exist before using them
        const addressLine = location?.gmbProfile?.storefrontAddress?.addressLines?.[0] || '';
        const locality = location?.gmbProfile?.storefrontAddress?.locality || '';
        const adminArea = location?.gmbProfile?.storefrontAddress?.administrativeArea || '';
        const country = location?.gmbProfile?.storefrontAddress?.country || '';
        const postalCode = location?.gmbProfile?.storefrontAddress?.postalCode || '';

        // Build address string only with available components and prevent duplication
        let addressParts = [];
        if (addressLine) addressParts.push(addressLine);
        if (locality) addressParts.push(locality);
        if (adminArea) addressParts.push(adminArea);
        if (postalCode) addressParts.push(postalCode);
        if (country) addressParts.push(country);

        const address = addressParts.length > 0 ? addressParts.join(', ') : 'No address available';

        // Return object with name and address properties
        return { name, address };
      }
      return { name: 'Unknown Location', address: '' };
    },

    getLocationId(rec) {
      if (rec.location) {
        return rec.location.location_id || rec.location.id;
      } else if (rec.affected_locations && rec.affected_locations.length > 0) {
        // Return comma-separated location IDs for bulk operations
        return rec.affected_locations.map(loc => loc.id || loc.location_id).join(',');
      }
      return '';
    },

    getAffectedLocations(rec) {
      if (rec.location_recommendations && rec.location_recommendations.length > 0) {
        return rec.location_recommendations;
      }
      return [];
    },

    getPriorityClass(priority) {
      if (!priority) return '';

      switch (priority.toLowerCase()) {
        case 'high':
          return 'text-red-600 font-medium';
        case 'medium':
          return 'text-orange-600';
        case 'low':
          return 'text-blue-600';
        default:
          return 'text-gray-600';
      }
    },

    getGroupedLocations(rec) {
      const locations = this.getAffectedLocations(rec);
      const groups = [];

      // Group 1: Locations that already match the recommended value
      const matchingLocations = locations.filter(location => {
        const currentValue = location.current_value || '';
        const recommendedValue = location.recommended_values?.recommended_value || '';
        return currentValue === recommendedValue && currentValue !== '';
      });

      if (matchingLocations.length > 0) {
        const sampleLoc = matchingLocations[0];
        groups.push({
          current_value: sampleLoc.current_value || '',
          recommended_value: sampleLoc.recommended_values?.recommended_value || '',
          locations: matchingLocations,
          status: 'matching'
        });
      }

      // Group 2: Locations with empty current values (missing values)
      const missingLocations = locations.filter(location => {
        const currentValue = location.current_value || '';
        return currentValue === '';
      });

      if (missingLocations.length > 0) {
        const sampleLoc = missingLocations[0];
        groups.push({
          current_value: '',
          recommended_value: sampleLoc.recommended_values?.recommended_value || '',
          locations: missingLocations,
          status: 'missing'
        });
      }

      // Group 3: Locations that need to be updated (have different values)
      const updateLocations = locations.filter(location => {
        const currentValue = location.current_value || '';
        const recommendedValue = location.recommended_values?.recommended_value || '';
        return currentValue !== recommendedValue && currentValue !== '';
      });

      // Further group the update locations by their current and recommended values
      const updateGroups = {};
      updateLocations.forEach(location => {
        const currentValue = location.current_value || '';
        const recommendedValue = location.recommended_values?.recommended_value || '';
        const key = `${currentValue}|||${recommendedValue}`;

        if (!updateGroups[key]) {
          updateGroups[key] = {
            current_value: currentValue,
            recommended_value: recommendedValue,
            locations: []
          };
        }

        updateGroups[key].locations.push(location);
      });

      // Add all update groups to main groups array
      Object.values(updateGroups).forEach(group => {
        groups.push(group);
      });

      return groups;
    },

    isLongText(text) {
      if (!text) return false;
      return text.length > 70; // Consider text longer than 100 characters as "long"
    },

    getFieldLabel(field) {
      if (!field) return 'Field';

      switch (field) {
        case 'website':
          return 'Website URL';
        case 'phone':
          return 'Phone Number';
        case 'primary_category':
          return 'Primary Category';
        case 'description':
          return 'Business Description';
        case 'name':
          return 'Business Name';
        default:
          return this.capitalize(field);
      }
    },
    getAddFieldText(field) {
      if (!field) return 'Add Field';

      switch (field) {
        case 'website':
          return 'Add Website URL';
        case 'phone':
          return 'Add Phone Number';
        case 'primary_category':
          return 'Add Primary Category';
        case 'description':
          return 'Add Business Description';
        case 'name':
          return 'Add Business Name';
        case 'address':
          return 'Add Address';
        case 'hours':
          return 'Add Business Hours';
        case 'attributes':
          return 'Add Business Attributes';
        default:
          return `Add ${this.capitalize(field)}`;
      }
    },
    async showUpdateResultsDialog(recommendation) {
      try {
        this.loading = true;
        this.currentRecommendation = recommendation;

        // Fetch the update results document
        const docRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "gmb-recommendations-updates",
          recommendation.id
        );

        const docSnapshot = await getDoc(docRef);

        if (!docSnapshot.exists()) {
          throw new Error("Update results not found");
        }

        const updateData = docSnapshot.data();

        if (!updateData?.updateResults) {
          throw new Error("Update results data is missing");
        }

        // Set the update results to the current recommendation
        this.currentRecommendation.updateResults = updateData.updateResults;
        this.showUpdateResults = true;
      } catch (error) {
        console.error("Error fetching update results:", error);
        this.$alert.show(`Could not retrieve update results: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    isPhoneWithoutRecommendation(rec) {
      // Check if it's a phone recommendation with generic message
      return rec.field === 'phone' && 
             (rec.location?.recommended_value?.includes('Please add a primary phone number') || 
              rec.recommended_value?.includes('Please add a primary phone number') ||
              (rec.location_recommendations && rec.location_recommendations.some(lr => 
                lr.recommended_values?.recommended_value?.includes('Please add a primary phone number'))));
    },
    handleConnectGMB() {
      try {
        this.$snackbar.show("Please wait a moment");
        this.loading = true;
        
        // Use the initConnection method from the connected.apps.mixins.js
        this.initConnection("google_business_profile", "bulk");
        
        setTimeout(() => {
          this.$snackbar.hide();
        }, 2000);
      } catch (error) {
        console.error("Error connecting Google Business Profile:", error);
        this.$alert.show("Failed to start Google Business connection. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    async checkBusinessDetails() {
      try {
        const brandDocRef = doc(db, "users", this.currentUID, "fly_workspaces", this.WID, "brand", "brand_details");
        const gmbSettingsCollectionRef = collection(db, "users", this.currentUID, "fly_workspaces", this.WID, "gmb-settings");
        
        const brandDocSnap = await getDoc(brandDocRef);
        const gmbSettingsQuery = query(gmbSettingsCollectionRef, orderBy("created_at", "desc"), limit(1));
        const gmbSettingsSnap = await getDocs(gmbSettingsQuery);

        let brandDetailsExist = brandDocSnap.exists() && Object.keys(brandDocSnap.data()).length > 0;
        let gmbSettingsExist = !gmbSettingsSnap.empty && gmbSettingsSnap.docs[0].data()?.gmb_details && Object.keys(gmbSettingsSnap.docs[0].data().gmb_details).length > 0;
        
        this.businessDetailsMissing = !(brandDetailsExist || gmbSettingsExist);

      } catch (error) {
        console.error("Error checking business details:", error);
        // Assume details are missing if there's an error, to prompt user
        this.businessDetailsMissing = true; 
      }
    },
    goToBusinessDetails() {
      // Assuming you have a route named 'business-details'
      // You might need to adjust this based on your actual router configuration
      this.$router.push({ name: 'brand' }); 
    },
  },
};
</script>

<style scoped>
.location-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

/* Add any additional styling you need */

/* Add status-filters styles from Location.vue */
.status-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.status-filter-btn {
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background-color: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.status-filter-btn:hover {
  background-color: #f7fafc;
}

.status-filter-btn.active {
  background-color: #8050DE;
  border-color: #6A3FBD;
  color: white;
}

/* Responsive styles */
@media (max-width: 768px) {
  .status-filter-btn {
    padding: 4px 8px;
    font-size: 12px;
  }
}
</style>